import { useCallback, useEffect, useRef, useState } from "react";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { useSearchParams } from "react-router-dom";
import moment from "moment";

import SortIcon from "../ui-icons/table/SortIcon";
import { urlDefault } from "src/constants";
import { siteUrl } from "src/api/config";
import Loading from "../Loading";
import { EmptyList } from "../EmptyList";

const AuditEmplyeesTable = (props: any) => {
  const { data, setExportData, setScrollDown, filters, customLoading } = props;
  const [sortData, setSortData] = useState<any>([]);
  const [query, setQuery] = useSearchParams();
  const [tableHeaderContent, setTableHeaderContent] = useState<any>({
    Title: {
      title: "Код документа",
      sort: true,
      width: "20%",
      sortDirection: "default",
      sortType: "string",
    },
    Ver: {
      title: "Версия документа",
      sort: false,
      width: "10%",
      sortDirection: "default",
      sortType: "number",
    },
    Created: {
      title: "Дата отправки на ознакомление",
      sort: true,
      width: "20%",
      sortDirection: "default",
      sortType: "date",
    },
    ReviewDate: {
      title: "Срок для ознакомления",
      sort: true,
      width: "15%",
      sortDirection: "default",
      sortType: "date",
    },
    Completed: {
      title: "Дата факта ознакомления",
      sort: true,
      width: "15%",
      sortDirection: "default",
      sortType: "date",
    },
    Status: {
      title: "Статус",
      sort: true,
      width: "10%",
      sortDirection: "default",
      sortType: "string",
    },
    TestAnswerRate: {
      title: "Результат тестирования",
      sort: true,
      width: "10%",
      sortDirection: "default",
      sortType: "number",
    },
  });
  const ITEMS_PER_PAGE = 10;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const openDocumentCard = (id: number) => {
    const newSearchParams = new URLSearchParams(query);
    newSearchParams.set("documentCard", id.toString());
    setQuery(newSearchParams);
  };

  const sortDataHandle = (
    sortField: string,
    sortDirection: string,
    sortType: string
  ) => {
    const tempTableHeaderContent = { ...tableHeaderContent };
    const tempData = [
      ...data.map((item: any) => {
        const isCancelled = item.Title.includes("Отклонено");

        return {
          ...item,
          ...{
            Title: item.Document.id,
            Status: isCancelled
              ? "Отклонено"
              : (item.Completed && "Ознакомлен") || "Не ознакомлен",
          },
        };
      }),
    ];
    Object.keys(tempTableHeaderContent).map((key: string) => {
      tempTableHeaderContent[key]["sortDirection"] = "default";
    });
    if (sortDirection === "default") {
      tempTableHeaderContent[sortField]["sortDirection"] = "desc";
    }
    if (sortDirection === "desc") {
      tempTableHeaderContent[sortField]["sortDirection"] = "asc";
    }
    if (sortDirection === "asc") {
      tempTableHeaderContent[sortField]["sortDirection"] = "default";
      setSortData([...data]);
    }
    if (sortType === "number") {
      if (tempTableHeaderContent[sortField]["sortDirection"] === "desc") {
        tempData.sort(
          (a: any, b: any) =>
            parseFloat(a[sortField]) - parseFloat(b[sortField])
        );
      }
      if (tempTableHeaderContent[sortField]["sortDirection"] === "asc") {
        tempData.sort(
          (a: any, b: any) =>
            parseFloat(b[sortField]) - parseFloat(a[sortField])
        );
      }
    }
    if (sortType === "string") {
      if (tempTableHeaderContent[sortField]["sortDirection"] === "desc") {
        tempData.sort((a: any, b: any) => {
          return a[sortField].localeCompare(b[sortField]);
        });
      }
      if (tempTableHeaderContent[sortField]["sortDirection"] === "asc") {
        tempData.sort((a: any, b: any) => {
          return b[sortField].localeCompare(a[sortField]);
        });
      }
    }
    if (sortType === "date") {
      if (tempTableHeaderContent[sortField]["sortDirection"] === "desc") {
        const distantFuture = new Date(8640000000000000);
        tempData.sort((a: any, b: any) => {
          let dateA = a[sortField] || distantFuture;
          let dateB = b[sortField] || distantFuture;
          return new Date(dateA).getTime() - new Date(dateB).getTime();
        });
      }
      if (tempTableHeaderContent[sortField]["sortDirection"] === "asc") {
        const distantFuture = new Date(8640000000000000);
        tempData.sort((a: any, b: any) => {
          let dateA = a[sortField] || distantFuture;
          let dateB = b[sortField] || distantFuture;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
      }
    }
    setTableHeaderContent({ ...tempTableHeaderContent });
    setSortData([...tempData]);
  };

  useEffect(() => {
    setSortData([...data]);
    const tempTableHeaderContent = { ...tableHeaderContent };
    Object.keys(tempTableHeaderContent).map((key: string) => {
      tempTableHeaderContent[key]["sortDirection"] = "default";
    });
    setTableHeaderContent({ ...tempTableHeaderContent });
  }, [data]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Триггерим за 100px до конца для плавности
    if (scrollHeight - scrollTop - clientHeight < 100) {
      setScrollDown(true);
      setVisibleCount(prev => {
        const newCount = prev + ITEMS_PER_PAGE;
        return newCount >= data.length ? data.length : newCount;
      });
    }
  }, [data.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [filters]);

  return (
    <FlexCol className="w-full">
      <FlexCol className="relative">
        <div
          ref={scrollContainerRef}
          id="owd_calendar_wrapper"
          className="flex flex-col relative pb-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] max-h-[calc(100vh-195px)] min-h-[60px]"
        >
          <FlexRow className="pb-5 sticky-custom max-h-[65px] min-w-[1500px] bg-white pt-5 px-10 gap-5 z-10">
            {Object.keys(tableHeaderContent).map((fieldKey) => {
              const key: string = fieldKey;
              return (
                <TableHeaderColumn
                  key={fieldKey}
                  sortField={fieldKey}
                  tableHeaderContent={tableHeaderContent[key]}
                  sortDataHandle={sortDataHandle}
                />
              );
            })}
          </FlexRow>
          <FlexCol className="px-10 divide-y grow min-w-[1500px]">
            {sortData.map((item: any) => (
              <FlexRow className="gap-5 px-2 py-4 -mx-2 w-full" key={item.ID}>
                <FlexCol
                  style={{ width: "20%" }}
                  onClick={() => openDocumentCard(item.Document.id)}
                  className="break-words text-[#637381] cursor-pointer"
                >
                  <span className="block text-sm font-normal not-italic text-[#637381]">
                    {item.DocumentCode}
                  </span>
                  <span className="block text-sm font-normal not-italic text-[#012169]">
                    {item.Document.Title}
                  </span>
                </FlexCol>
                <FlexCol
                  style={{ width: "10%" }}
                  className="break-words text-[#637381]"
                >
                  <span className="block text-sm font-normal not-italic text-[#637381]">
                    {((item.VersionDocument && item.VersionDocument.Ver) || (item.Document && item.Document.Ver) || 1)}
                  </span>
                </FlexCol>
                <FlexCol
                  style={{ width: "20%" }}
                  className="break-words text-[#637381]"
                >
                  <span className="block text-sm font-normal not-italic text-[#637381]">
                    {moment(item.Created).format("DD.MM.YYYY")}
                  </span>
                </FlexCol>
                <FlexCol
                  style={{ width: "15%" }}
                  className="break-words text-[#637381]"
                >
                  <span className="block text-sm font-normal not-italic text-[#637381]">
                    {moment(item.ReviewDate).format("DD.MM.YYYY")}
                  </span>
                </FlexCol>
                <FlexCol
                  style={{ width: "15%" }}
                  className="break-words text-[#637381]"
                >
                  <span className="block text-sm font-normal not-italic text-[#637381]">
                    {(item.Completed &&
                      moment(item.Completed).format("DD.MM.YYYY")) ||
                      "-"}
                  </span>
                </FlexCol>
                <FlexCol
                  style={{ width: "10%" }}
                  className="break-words text-[#637381]"
                >
                  <span className="block text-sm font-normal not-italic text-[#637381]">
                    {typeof item.Title === 'string' && item.Title.includes("Отклонено")
                      ? "Отклонено"
                      : (item.Completed && "Ознакомлен") || "Не ознакомлен"}
                  </span>
                </FlexCol>
                <FlexCol
                  style={{ width: "10%" }}
                  className="break-words text-[#637381]"
                >
                  <span className="block text-sm font-normal not-italic text-[#637381]">
                    {(item.TestAnswerRate && item.TestAnswerRate + "%") || "-"}
                  </span>
                </FlexCol>
              </FlexRow>
            ))}
          </FlexCol>
        </div>
        {customLoading &&
          <FlexCol className="w-full h-full items-center justify-center min-h-[170px] max-h-[calc(100vh-210px)]">
            <Loading />
          </FlexCol>
        }
        {!customLoading && data.length === 0 &&
          <FlexCol className="w-full h-full items-center justify-center min-h-[170px] max-h-[calc(100vh-210px)]">
            <EmptyList title="Данные не найдены" />
          </FlexCol>
        }
      </FlexCol>
    </FlexCol>
  );
};

export default AuditEmplyeesTable;

export function TableHeaderColumn({
  tableHeaderContent,
  sortDataHandle,
  sortField,
}: any) {
  return (
    <FlexCol
      className="select-none"
      style={{ width: tableHeaderContent.width }}
    >
      <FlexRow className="gap-x-2 w-full">
        {tableHeaderContent.icon && (
          <FlexCol>{tableHeaderContent.icon}</FlexCol>
        )}
        <FlexCol className="font-medium whitespace-pre-line">
          {tableHeaderContent.title}
        </FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol
            className="w-auto cursor-pointer"
            onClick={() =>
              sortDataHandle(
                sortField,
                tableHeaderContent.sortDirection,
                tableHeaderContent.sortType
              )
            }
          >
            <SortIcon sortDirection={tableHeaderContent.sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}
